// REGISTRAR — A2P Compliance Setup Assistant.
//
// Walks the agency owner through SMS / A2P 10DLC registration:
// brand info → generated copy preview → URL audit → final pre-submit
// checklist → copy-paste packet ready for Twilio.
//
// Spec: docs/agents/registrar_a2p_setup.md
//
// State persists to localStorage so the owner can leave + return.

import React from 'react';
import {
  ShieldCheck, Copy, CheckCircle2, AlertTriangle, Loader2,
  ChevronRight, ChevronLeft, RefreshCcw, ExternalLink, X,
} from 'lucide-react';
import { bearer } from '../lib/authedFetch';

const LS_KEY = 'registrar.formState.v1';

const VERTICALS = [
  { key: 'life',     label: 'Life' },
  { key: 'health',   label: 'Health' },
  { key: 'medicare', label: 'Medicare' },
  { key: 'aca',      label: 'ACA' },
  { key: 'pc',       label: 'P&C (Auto/Home)' },
  { key: 'all',      label: 'All Lines' },
];

const USE_CASES = [
  { key: 'marketing',     label: 'Marketing',     desc: 'Quote follow-ups, promotions, lead nurture' },
  { key: 'customer_care', label: 'Customer Care', desc: 'Appointment reminders, policy updates, support — transactional only' },
  { key: 'mixed',         label: 'Mixed',         desc: 'Both marketing + customer care in the same campaign' },
];

function copyToClipboard(text, onDone) {
  if (!navigator.clipboard) { onDone?.('Clipboard unavailable in this browser'); return; }
  navigator.clipboard.writeText(text).then(
    () => onDone?.('Copied!'),
    () => onDone?.('Copy failed'),
  );
}

export default function A2pSetupView({ currentUser, settings = {}, showToast = () => {} }) {
  const [form, setForm] = React.useState(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return {
      agencyName:    settings?.ownerName || '',
      vertical:      'health',
      website:       '',
      agentPhone:    '',
      supportEmail:  '',
      privacyUrl:    '',
      termsUrl:      '',
      useCase:       'marketing',
      directLending: false,
    };
  });
  React.useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(form)); } catch {}
  }, [form]);

  const [step, setStep] = React.useState(0);  // 0=brand, 1=preview, 2=audit, 3=submit
  const [draft, setDraft] = React.useState(null);
  const [draftLoading, setDraftLoading] = React.useState(false);
  const [audit, setAudit] = React.useState(null);
  const [auditLoading, setAuditLoading] = React.useState(false);
  const [checks, setChecks] = React.useState({}); // operator's manual ✓ on the final checklist

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Generate the draft anytime brand info changes + step is on preview.
  const regenerateDraft = React.useCallback(async () => {
    setDraftLoading(true);
    try {
      const res = await fetch('/api/registrar/draft', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setDraft(json);
    } catch (err) {
      showToast(`Draft failed: ${err.message || err}`, 'error');
    } finally {
      setDraftLoading(false);
    }
  }, [form, showToast]);
  React.useEffect(() => {
    if (step >= 1 && form.agencyName) regenerateDraft();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, form.agencyName, form.vertical, form.website, form.agentPhone, form.useCase, form.directLending, form.privacyUrl, form.termsUrl]);

  async function runAudit() {
    setAuditLoading(true);
    try {
      const res = await fetch('/api/registrar/audit', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...(await bearer()) },
        body: JSON.stringify({ websiteUrl: form.website, privacyUrl: form.privacyUrl, termsUrl: form.termsUrl }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setAudit(json);
    } catch (err) {
      showToast(`Audit failed: ${err.message || err}`, 'error');
    } finally {
      setAuditLoading(false);
    }
  }

  function fullPacketMarkdown() {
    if (!draft) return '';
    const sm = (draft.sample_messages || []).map((m, i) => `${i + 1}. ${m}`).join('\n');
    return `# A2P 10DLC Registration Packet — ${draft.brand?.name || form.agencyName}

## Brand
- Business name: ${draft.brand?.name}
- Website: ${draft.brand?.website || '(missing)'}
- Business phone: ${draft.brand?.agent_phone || '(missing)'}
- Support email: ${form.supportEmail || '(missing)'}
- Privacy Policy URL: ${draft.brand?.privacy_url || '(missing)'}
- Terms URL: ${draft.brand?.terms_url || '(missing)'}
- Vertical: ${draft.brand?.vertical}

## Campaign
- Use-case: ${draft.use_case}
- Direct lending: ${draft.direct_lending ? 'Yes' : 'No'}
- Contains links: ${draft.content_flags?.contains_links ? 'Yes' : 'No'}
- Contains phone numbers: ${draft.content_flags?.contains_phone_numbers ? 'Yes' : 'No'}

## Campaign description
${draft.campaign_description}

## Sample messages
${sm}

## End-user opt-in flow
${draft.opt_in_flow}

## Opt-in keywords
${(draft.opt_in_keywords || []).join(', ')}

## Opt-in auto-reply
${draft.opt_in_auto_reply}

## Website consent checkbox copy
${draft.website_consent_copy}
`;
  }

  const headerStep = ['Brand Info', 'Generated Copy', 'URL Audit', 'Final Review'][step];

  return (
    <div className="max-w-none mx-auto px-4 py-4 space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-950 to-emerald-950/40 border border-emerald-900/30 rounded-xl p-5 text-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-400/80 flex items-center gap-2">
              <ShieldCheck size={11}/> REGISTRAR · A2P compliance setup
            </div>
            <h1 className="text-xl font-black mt-1.5">Get your SMS approved the first time.</h1>
            <p className="text-xs text-slate-400 mt-1">
              Step {step + 1} of 4 · {headerStep}
            </p>
          </div>
          <button onClick={() => { localStorage.removeItem(LS_KEY); window.location.reload(); }} className="text-xs text-emerald-200/70 hover:text-emerald-200 inline-flex items-center gap-1">
            <RefreshCcw size={11}/> Start over
          </button>
        </div>

        {/* Progress dots */}
        <div className="mt-4 flex items-center gap-2">
          {['Brand', 'Copy', 'Audit', 'Submit'].map((label, i) => (
            <React.Fragment key={label}>
              <button
                onClick={() => setStep(i)}
                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${step === i ? 'bg-emerald-500 text-black' : i < step ? 'bg-emerald-900/40 text-emerald-200' : 'bg-slate-900/40 text-slate-500'}`}
              >
                {i + 1}. {label}
              </button>
              {i < 3 && <div className="h-px flex-1 bg-emerald-900/30"/>}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step body */}
      {step === 0 && <BrandStep form={form} update={update} onNext={() => setStep(1)}/>}
      {step === 1 && <PreviewStep draft={draft} loading={draftLoading} onBack={() => setStep(0)} onNext={() => setStep(2)} onCopy={(text, label) => copyToClipboard(text, m => showToast(`${label}: ${m}`))}/>}
      {step === 2 && <AuditStep audit={audit} loading={auditLoading} onRun={runAudit} onBack={() => setStep(1)} onNext={() => setStep(3)} form={form}/>}
      {step === 3 && (
        <SubmitStep
          form={form}
          draft={draft}
          audit={audit}
          checks={checks}
          setChecks={setChecks}
          onCopyAll={() => copyToClipboard(fullPacketMarkdown(), m => showToast(`Full packet: ${m}`))}
          onBack={() => setStep(2)}
        />
      )}
    </div>
  );
}

// ── STEP 0 — Brand info form ────────────────────────────────
function BrandStep({ form, update, onNext }) {
  const required = ['agencyName','website','agentPhone','supportEmail','privacyUrl','termsUrl'];
  const missing = required.filter(k => !String(form[k] || '').trim());
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Brand Information</div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Agency name" req>
          <input type="text" value={form.agencyName} onChange={e => update('agencyName', e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 focus:border-emerald-400 focus:outline-none" placeholder="ABC Insurance Agency, LLC"/>
        </Field>
        <Field label="Vertical" req>
          <select value={form.vertical} onChange={e => update('vertical', e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 bg-white">
            {VERTICALS.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
          </select>
        </Field>
        <Field label="Website (live URL)" req>
          <input type="url" value={form.website} onChange={e => update('website', e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 focus:border-emerald-400 focus:outline-none" placeholder="https://abcagency.com"/>
        </Field>
        <Field label="Business phone (display)" req>
          <input type="tel" value={form.agentPhone} onChange={e => update('agentPhone', e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 focus:border-emerald-400 focus:outline-none" placeholder="(555) 123-4567"/>
        </Field>
        <Field label="Support email" req>
          <input type="email" value={form.supportEmail} onChange={e => update('supportEmail', e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 focus:border-emerald-400 focus:outline-none" placeholder="support@abcagency.com"/>
        </Field>
        <Field label="Privacy Policy URL" req hint="Must contain SMS clauses. We'll scan it for you.">
          <input type="url" value={form.privacyUrl} onChange={e => update('privacyUrl', e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 focus:border-emerald-400 focus:outline-none" placeholder="https://abcagency.com/privacy"/>
        </Field>
        <Field label="Terms & Conditions URL" req hint="Must contain STOP/HELP + consent-not-a-condition language.">
          <input type="url" value={form.termsUrl} onChange={e => update('termsUrl', e.target.value)} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 focus:border-emerald-400 focus:outline-none" placeholder="https://abcagency.com/terms"/>
        </Field>
      </div>

      <div className="border-t border-slate-200 pt-4 space-y-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Campaign Use-Case</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {USE_CASES.map(uc => (
            <button
              key={uc.key}
              onClick={() => update('useCase', uc.key)}
              className={`text-left border rounded-lg px-3 py-2 ${form.useCase === uc.key ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-400'}`}
            >
              <div className="text-sm font-bold text-slate-900">{uc.label}{form.useCase === uc.key && <span className="text-emerald-700 ml-1">✓</span>}</div>
              <div className="text-[11px] text-slate-600 mt-0.5">{uc.desc}</div>
            </button>
          ))}
        </div>
        <label className={`flex items-start gap-2 border rounded-lg px-3 py-2 cursor-pointer ${form.directLending ? 'border-amber-500 bg-amber-50' : 'border-slate-200'}`}>
          <input type="checkbox" checked={form.directLending} onChange={e => update('directLending', e.target.checked)} className="mt-1 h-4 w-4 accent-amber-600"/>
          <div>
            <div className="text-sm font-bold text-slate-900">My agency offers loans, financing, payday loans, credit, or mortgage lending</div>
            <div className="text-[11px] text-slate-600">Default: NO. Insurance agencies that miscategorize as lending get rejected by the carrier. Only check this if you literally offer credit products.</div>
          </div>
        </label>
      </div>

      <div className="border-t border-slate-200 pt-4 flex items-center justify-between gap-4">
        {missing.length > 0 ? (
          <div className="text-[11px] text-amber-700">⚠ Fill in: {missing.join(', ')}</div>
        ) : (
          <div className="text-[11px] text-emerald-700">✓ Brand info ready. Continue to preview the generated copy.</div>
        )}
        <button onClick={onNext} disabled={missing.length > 0} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-bold rounded-lg px-4 py-2 inline-flex items-center gap-1.5">
          Next: Generated Copy <ChevronRight size={14}/>
        </button>
      </div>
    </div>
  );
}

function Field({ label, req, hint, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1">
        {label}{req && <span className="text-rose-500">*</span>}
      </span>
      {children}
      {hint && <span className="text-[10px] text-slate-500 mt-0.5 block">{hint}</span>}
    </label>
  );
}

// ── STEP 1 — Generated copy preview ─────────────────────────
function PreviewStep({ draft, loading, onBack, onNext, onCopy }) {
  if (loading && !draft) {
    return <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500"><Loader2 size={20} className="inline animate-spin mr-2"/>Drafting your registration copy…</div>;
  }
  if (!draft) {
    return <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">No draft yet. Go back and fill in the brand info.</div>;
  }
  return (
    <div className="space-y-3">
      <CopyBlock label="Campaign Description" text={draft.campaign_description} onCopy={onCopy}/>
      <CopyBlock
        label={`Sample Messages (${(draft.sample_messages || []).length})`}
        text={(draft.sample_messages || []).map((m, i) => `${i + 1}. ${m}`).join('\n\n')}
        onCopy={onCopy}
      />
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Content Flags (the carrier will ask)</div>
        <div className="text-sm text-slate-800">
          Contains embedded links: <strong className={draft.content_flags?.contains_links ? 'text-emerald-700' : 'text-slate-500'}>{draft.content_flags?.contains_links ? 'Yes' : 'No'}</strong>
        </div>
        <div className="text-sm text-slate-800">
          Contains phone numbers: <strong className={draft.content_flags?.contains_phone_numbers ? 'text-emerald-700' : 'text-slate-500'}>{draft.content_flags?.contains_phone_numbers ? 'Yes' : 'No'}</strong>
        </div>
      </div>
      <CopyBlock label="End-User Opt-In Flow" text={draft.opt_in_flow} onCopy={onCopy}/>
      <CopyBlock label="Opt-In Keywords" text={(draft.opt_in_keywords || []).join(', ')} onCopy={onCopy}/>
      <CopyBlock label="Opt-In Auto-Reply" text={draft.opt_in_auto_reply} onCopy={onCopy}/>
      <CopyBlock label="Website Consent Checkbox Copy" text={draft.website_consent_copy} onCopy={onCopy}/>
      {draft.warnings?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">⚠ Warnings</div>
          {draft.warnings.map((w, i) => <div key={i} className="text-xs text-amber-900">{w}</div>)}
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <button onClick={onBack} className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft size={14}/> Back</button>
        <button onClick={onNext} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-lg px-4 py-2 inline-flex items-center gap-1.5">
          Next: URL Audit <ChevronRight size={14}/>
        </button>
      </div>
    </div>
  );
}

function CopyBlock({ label, text, onCopy }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
        <button onClick={() => onCopy(text, label)} className="text-[11px] font-bold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1">
          <Copy size={11}/> Copy
        </button>
      </div>
      <pre className="text-xs text-slate-800 whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-md p-2.5 font-sans leading-relaxed">{text}</pre>
    </div>
  );
}

// ── STEP 2 — URL audit ──────────────────────────────────────
function AuditStep({ audit, loading, onRun, onBack, onNext, form }) {
  return (
    <div className="space-y-3">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">URL Audit</div>
        <p className="text-xs text-slate-600 mb-3">
          We fetch your privacy policy + terms URLs and scan the HTML for the SMS clauses the carrier requires. JavaScript-rendered pages (React/Vue) return little parseable content — we'll flag low-confidence scans.
        </p>
        <div className="text-xs text-slate-700 space-y-1 mb-3">
          <div>Website: <a href={form.website} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-0.5">{form.website || '(none)'} <ExternalLink size={10}/></a></div>
          <div>Privacy Policy: <a href={form.privacyUrl} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-0.5">{form.privacyUrl || '(none)'} <ExternalLink size={10}/></a></div>
          <div>Terms: <a href={form.termsUrl} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-0.5">{form.termsUrl || '(none)'} <ExternalLink size={10}/></a></div>
        </div>
        <button onClick={onRun} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-bold rounded-lg px-4 py-2 inline-flex items-center gap-1.5">
          {loading ? <><Loader2 size={14} className="animate-spin"/> Auditing…</> : <>Run audit</>}
        </button>
      </div>

      {audit && (
        <>
          <AuditCard title="Website" result={audit.website} reqs={null}/>
          <AuditCard title="Privacy Policy" result={audit.privacy} reqs={audit.privacy?.requirements}/>
          <AuditCard title="Terms & Conditions" result={audit.terms}     reqs={audit.terms?.requirements}/>
        </>
      )}

      <div className="flex items-center justify-between gap-4">
        <button onClick={onBack} className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft size={14}/> Back</button>
        <button onClick={onNext} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-lg px-4 py-2 inline-flex items-center gap-1.5">
          Next: Final Review <ChevronRight size={14}/>
        </button>
      </div>
    </div>
  );
}

function AuditCard({ title, result, reqs }) {
  if (!result) return null;
  const ok = result.live;
  return (
    <div className={`border rounded-xl p-4 ${ok ? 'border-emerald-200 bg-white' : 'border-rose-200 bg-rose-50/30'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-bold text-sm text-slate-900 flex items-center gap-1.5">
          {ok ? <CheckCircle2 size={14} className="text-emerald-600"/> : <X size={14} className="text-rose-600"/>}
          {title}
        </div>
        <div className="text-[11px] text-slate-500">
          {ok ? `HTTP ${result.status}` : result.error || 'unreachable'}
        </div>
      </div>
      {reqs && (
        <ul className="space-y-0.5 mt-2">
          {reqs.map(r => (
            <li key={r.id} className="text-xs flex items-center gap-1.5">
              {r.present ? <CheckCircle2 size={11} className="text-emerald-600"/> : <X size={11} className="text-rose-600"/>}
              <span className={r.present ? 'text-slate-700' : 'text-rose-700 font-semibold'}>{r.label}</span>
            </li>
          ))}
        </ul>
      )}
      {result.warnings?.length > 0 && (
        <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          {result.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
        </div>
      )}
    </div>
  );
}

// ── STEP 3 — Final pre-submit checklist + full packet copy ─
const CHECKLIST = [
  ['business_matches',  'Business name matches website'],
  ['website_live',      'Website is live'],
  ['privacy_live',      'Privacy Policy URL is live'],
  ['terms_live',        'Terms URL is live'],
  ['samples_match',     'Sample messages match the actual campaign'],
  ['flags_correct',     'Links / phone number fields are marked correctly'],
  ['no_lending',        'Direct lending marked No (unless agency offers loans)'],
  ['opt_in_clear',      'Opt-in flow clearly explains how consent is collected'],
  ['not_pre_checked',   'Opt-in checkbox is NOT pre-checked'],
  ['stop_help_in_copy', 'STOP / HELP language in sample messages'],
  ['freq_rates',        'Message frequency + data rates disclosures included'],
];

function SubmitStep({ form, draft, audit, checks, setChecks, onCopyAll, onBack }) {
  const toggle = (k) => setChecks(c => ({ ...c, [k]: !c[k] }));
  const allChecked = CHECKLIST.every(([k]) => checks[k]);
  // Auto-populate the audit-driven items if we have audit results.
  React.useEffect(() => {
    if (!audit) return;
    setChecks(c => ({
      ...c,
      website_live: audit.website?.live || c.website_live,
      privacy_live: audit.privacy?.live || c.privacy_live,
      terms_live:   audit.terms?.live   || c.terms_live,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audit]);

  return (
    <div className="space-y-3">
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Pre-Submit Checklist</div>
        <ul className="space-y-1.5">
          {CHECKLIST.map(([k, label]) => (
            <li key={k}>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={!!checks[k]} onChange={() => toggle(k)} className="mt-1 h-4 w-4 accent-emerald-600"/>
                <span className={`text-sm ${checks[k] ? 'text-emerald-700' : 'text-slate-700'}`}>{label}</span>
              </label>
            </li>
          ))}
        </ul>
        {!allChecked && (
          <div className="text-[11px] text-amber-700 flex items-center gap-1">
            <AlertTriangle size={11}/> {CHECKLIST.length - CHECKLIST.filter(([k]) => checks[k]).length} item(s) remaining before submission.
          </div>
        )}
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
        <div className="flex items-center justify-between gap-4 mb-2">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Copy-and-paste registration packet</div>
            <div className="text-xs text-slate-700 mt-0.5">Everything in one block, ready to paste into the A2P 10DLC or Toll-Free Verification form.</div>
          </div>
          <button
            onClick={onCopyAll}
            disabled={!draft}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-bold rounded-lg px-4 py-2 inline-flex items-center gap-1.5 whitespace-nowrap"
          >
            <Copy size={14}/> Copy full packet
          </button>
        </div>
        <div className="text-[11px] text-slate-600">
          Brand: <strong>{form.agencyName}</strong> · Vertical: {form.vertical} · Use-case: {form.useCase} · {(draft?.sample_messages || []).length} sample messages.
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <button onClick={onBack} className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft size={14}/> Back to Audit</button>
        <a href="https://console.twilio.com/us1/develop/sms/regulatory-compliance/a2p-10dlc" target="_blank" rel="noopener noreferrer" className="text-sm text-emerald-700 hover:text-emerald-800 font-bold inline-flex items-center gap-1">
          Open A2P Registration Console <ExternalLink size={12}/>
        </a>
      </div>
    </div>
  );
}
